import { cn } from '@/utilities/cn'
import * as React from 'react'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  // Extended to support former DetailCard options
  variant?: 'default' | 'glass' | 'sidebar' | 'main'
  title?: string
  actions?: React.ReactNode
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', title, actions, children, ...props }, ref) => {
    const baseClasses = 'rounded-lg border shadow-sm overflow-hidden'

    const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
      default: 'bg-card text-card-foreground',
      glass: 'bg-white/80 backdrop-blur-sm border-slate-200',
      sidebar: 'bg-white/80 backdrop-blur-sm border-slate-200 shadow-soft',
      main: 'bg-white border-slate-200',
    }

    return (
      <div className={cn(baseClasses, variantClasses[variant], className)} ref={ref} {...props}>
        {(title || actions) && (
          <div className={cn('flex items-center justify-between px-6 py-4 mb-4', title && 'border-b border-slate-200')}>
            {title && <h3 className="text-xl font-bold text-slate-900">{title}</h3>}
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        )}
        {title || actions ? (
          <div className="px-6 pb-6">
            {children}
          </div>
        ) : (
          <>{children}</>
        )}
      </div>
    )
  },
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div className={cn('flex flex-col space-y-1.5 p-6', className)} ref={ref} {...props} />
  ),
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      ref={ref}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p className={cn('text-sm text-muted-foreground', className)} ref={ref} {...props} />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div className={cn('p-6', className)} ref={ref} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div className={cn('flex items-center p-6 pt-0', className)} ref={ref} {...props} />
  ),
)
CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }
